// Deterministic, high-signal reflection seeds for Declination.
// No hallucinated authority. These are pattern-based mirrors, not diagnoses.
// The user is always the only authority on their own work.

import type { Decision, Pull, CueCategory } from './types'
import { CUE_LABELS, CUE_IS_STATUS } from './types'

export function generateSeedAngles(decision: Decision): string[] {
  const seeds: string[] = []
  const pulls = decision.pulls || []
  const statusPulls = pulls.filter(p => CUE_IS_STATUS[p.cueCategory])
  const truthPulls = pulls.filter(p => !CUE_IS_STATUS[p.cueCategory])
  const totalStrength = pulls.reduce((s, p) => s + p.strength, 0) || 1
  const statusWeight = statusPulls.reduce((s, p) => s + p.strength, 0) / totalStrength
  const decl = decision.declination || 0

  // Always start from the actual logged data. Never invent inner states.

  if (statusWeight > 0.65) {
    seeds.push(
      `In this decision, status-oriented cues carried more than 65% of the total pull strength. Look at the three strongest status pulls and ask: what concrete client or audience outcome would have been different if those three had been half as strong?`
    )
  }

  if (decl > 55) {
    seeds.push(
      `The declination here was ${Math.round(decl)}°. The composite bearing of the pulls sat quite far from the private north you wrote. Choose the single pull that contributed most to that gap and write one sentence about what would have happened to the work if that pull had been absent.`
    )
  }

  if (truthPulls.length === 0) {
    seeds.push(
      `No pulls in the "craft truth" or "energy" categories were logged. Before the next similar decision, what is one observable signal in the first 10 days of the work that would tell you whether the direction is serving the actual outcome?`
    )
  }

  if (decision.hesitationNote && decision.hesitationNote.length > 12) {
    seeds.push(
      `You captured a hesitation: "${decision.hesitationNote.slice(0, 110)}${decision.hesitationNote.length > 110 ? '…' : ''}". Which of the pulls you placed on the compass most directly fed that hesitation? What would the bearing have been without it?`
    )
  }

  // Precedent vs visibility tension (common freelance pattern)
  const hasPrecedent = pulls.some(p => p.cueCategory === 'precedent')
  const hasVisibility = pulls.some(p => p.cueCategory === 'visibility')
  if (hasPrecedent && hasVisibility && statusWeight > 0.5) {
    seeds.push(
      `You logged both precedent and visibility pulls. In the three most recent decisions where precedent was the stronger of the two, how did the patience curve (speed and quality of external signals) compare to the visibility-dominant ones?`
    )
  }

  // Calibration seed
  if (decision.releasedAt && decision.expectedVelocity) {
    seeds.push(
      `You marked an expected velocity of ${decision.expectedVelocity}/5 at release. When you look at the signals logged so far, is the actual arrival curve running ahead, behind, or roughly where you expected? What cue from the original compass most predicted the actual pace?`
    )
  }

  // Gentle default if nothing specific triggered
  if (seeds.length === 0) {
    seeds.push(
      `Look at the strongest single pull on this compass (regardless of category). In the first two weeks after release, which incoming signals, if any, seem to have been shaped by that cue? Write one sentence connecting them.`
    )
    seeds.push(
      `Re-read the private north you wrote at the time. Now that some time and signals have passed, does the work still feel like it moved toward that north, or did it move somewhere else? What (if anything) would you adjust in how you weight cues next time?`
    )
  }

  // Never more than 3. Keep them short, specific, and answerable only by the person who lived the decision.
  return seeds.slice(0, 3)
}

// Optional: a tiny helper if someone wants "another angle" without AI.
export function generateAlternativeAngle(decision: Decision): string {
  const pulls = decision.pulls || []
  if (pulls.length === 0) return "Add at least one pull to the compass first, then ask again."

  const weakestTruth = [...pulls]
    .filter(p => !CUE_IS_STATUS[p.cueCategory])
    .sort((a, b) => a.strength - b.strength)[0]

  if (weakestTruth) {
    return `You placed a truth-oriented pull ("${CUE_LABELS[weakestTruth.cueCategory]}") at only ${weakestTruth.strength} strength. What would the decision have looked like if that pull had been the dominant one (80+)? Write the first sentence of the brief or proposal that version would have produced.`
  }

  const strongestStatus = [...pulls]
    .filter(p => CUE_IS_STATUS[p.cueCategory])
    .sort((a, b) => b.strength - a.strength)[0]

  if (strongestStatus) {
    return `The strongest status pull was "${CUE_LABELS[strongestStatus.cueCategory]}". Name one real-world constraint or audience reality that this cue made invisible at the time of the decision.`
  }

  return "Consider the single cue you wrote the most specific detail for. How would an outside observer (a peer who only saw the final work, never the brief) be able to tell whether that cue shaped the outcome?"
}
